import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProfile, updateProfile, deleteAccount, changePassword, getModalities, getSessionTypes, createSessionType, deleteSessionType, getGroups, getGroupSessionTypes, getOfferings, updateOffering, getOnboardingQuestions, saveOnboardingQuestions } from '../api/client';
import ModalitySelector, { computeInitialOpenGroups } from '../components/ModalitySelector';
import OfferingFormModal from '../components/OfferingFormModal';
import LoadingLeaf from '../components/LoadingLeaf';

const timezoneOptions = typeof Intl.supportedValuesOf === 'function'
  ? Intl.supportedValuesOf('timeZone')
  : [Intl.DateTimeFormat().resolvedOptions().timeZone];

export default function Settings() {
  const { user, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('practice');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const PASSWORD_REQUIREMENTS = 'At least 8 characters, including one uppercase letter and one number.';
  const isPasswordValid = (pw) => pw.length >= 8 && /[A-Z]/.test(pw) && /[0-9]/.test(pw);

  const [modalityGroups, setModalityGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [openGroupsInitialized, setOpenGroupsInitialized] = useState(false);

  const [sessionTypes, setSessionTypes] = useState([]);
  const [groupTypeOptions, setGroupTypeOptions] = useState([]);
  const [showAddTypeRow, setShowAddTypeRow] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [copyFromId, setCopyFromId] = useState('');

  const [offerings, setOfferings] = useState([]);
  // Client onboarding questions: edited as a local draft, saved as a whole list.
  const [questions, setQuestions] = useState([]);
  const [questionsDirty, setQuestionsDirty] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showOfferingModal, setShowOfferingModal] = useState(false);
  const [editingOffering, setEditingOffering] = useState(null); // null = create mode

  const selectedModalities = useMemo(() => (
    profile
      ? (profile.modalities && profile.modalities.length ? profile.modalities : (profile.modality ? [profile.modality] : []))
      : []
  ), [profile]);

  const toggleModality = (name) => {
    setProfile(p => {
      const current = p.modalities && p.modalities.length ? p.modalities : (p.modality ? [p.modality] : []);
      const next = current.includes(name) ? current.filter(x => x !== name) : [...current, name];
      return { ...p, modalities: next, modality: next[0] || '' };
    });
  };

  useEffect(() => {
    fetchProfile();
    fetchModalities();
    fetchSessionTypes();
    fetchGroupTypeOptions();
    fetchOfferings();
    fetchQuestions();
  }, []);

  // Expand any group that already has a selection, once, after both profile and modality groups have loaded
  useEffect(() => {
    if (openGroupsInitialized || !profile || modalityGroups.length === 0) return;
    setOpenGroups(computeInitialOpenGroups(modalityGroups, selectedModalities));
    setOpenGroupsInitialized(true);
  }, [profile, modalityGroups, openGroupsInitialized, selectedModalities]);

  const fetchProfile = async () => {
    try {
      const res = await getProfile();
      setProfile(res.data);
    } catch (err) {
      setError('Could not load profile.');
    } finally {
      setLoading(false);
    }
  };

  const fetchModalities = async () => {
    try {
      const res = await getModalities();
      setModalityGroups(res.data || []);
    } catch (err) {
      // Non-critical — the rest of Settings still works without modality descriptions.
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await getOnboardingQuestions();
      setQuestions((res.data || []).map(q => ({ question: q.question || '', hint: q.hint || '' })));
      setQuestionsDirty(false);
    } catch (err) {
      // Non-critical — the card just shows empty until it loads.
    }
  };

  const MAX_QUESTIONS = 4;
  const editQuestions = (fn) => { setQuestions(fn); setQuestionsDirty(true); };
  const moveQuestion = (i, dir) => editQuestions(qs => {
    const next = [...qs];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    return next;
  });

  const handleSaveQuestions = async () => {
    setSavingQuestions(true);
    try {
      const res = await saveOnboardingQuestions(questions.filter(q => q.question.trim()));
      setQuestions((res.data || []).map(q => ({ question: q.question || '', hint: q.hint || '' })));
      setQuestionsDirty(false);
      showSuccess('Onboarding questions saved');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save onboarding questions.');
    } finally {
      setSavingQuestions(false);
    }
  };

  const fetchSessionTypes = async () => {
    try {
      const res = await getSessionTypes();
      setSessionTypes(res.data || []);
    } catch (err) {
      // Non-critical — the rest of Settings still works without 1:1 session types.
    }
  };

  const fetchGroupTypeOptions = async () => {
    try {
      const groupsRes = await getGroups();
      const groups = groupsRes.data || [];
      const perGroup = await Promise.all(groups.map(async g => {
        try {
          const res = await getGroupSessionTypes(g.id);
          return (res.data || []).map(t => ({ id: t.id, name: t.name, groupName: g.name }));
        } catch {
          return [];
        }
      }));
      setGroupTypeOptions(perGroup.flat());
    } catch (err) {
      // Non-critical — coaches can still add 1:1 session types from scratch.
    }
  };

  const handleAddSessionType = async () => {
    if (!newTypeName.trim()) return;
    try {
      const res = await createSessionType(newTypeName.trim());
      setSessionTypes(prev => [...prev, res.data]);
      setNewTypeName('');
      setCopyFromId('');
      setShowAddTypeRow(false);
    } catch (err) {
      setError('Could not add session type.');
    }
  };

  const handleRemoveSessionType = async (typeId) => {
    try {
      await deleteSessionType(typeId);
      setSessionTypes(prev => prev.filter(t => t.id !== typeId));
    } catch (err) {
      setError('Could not remove session type.');
    }
  };

  const fetchOfferings = async () => {
    try {
      const res = await getOfferings();
      setOfferings(res.data || []);
    } catch (err) {
      // Non-critical — session creation still works without Offerings seeded.
    }
  };

  const handleToggleOfferingActive = async (offering) => {
    try {
      const res = await updateOffering(offering.id, { is_active: !offering.is_active });
      setOfferings(prev => prev.map(o => o.id === offering.id ? res.data : o));
    } catch (err) {
      setError('Could not update offering.');
    }
  };

  const handleOfferingSaved = (savedOffering) => {
    setOfferings(prev => {
      const exists = prev.some(o => o.id === savedOffering.id);
      return exists ? prev.map(o => o.id === savedOffering.id ? savedOffering : o) : [...prev, savedOffering];
    });
    setShowOfferingModal(false);
    setEditingOffering(null);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profile);
      await refreshProfile();
      showSuccess('Workspace profile saved.');
    } catch (err) {
      setError('Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!isPasswordValid(newPassword)) {
      setPasswordError(`Password doesn't meet requirements: ${PASSWORD_REQUIREMENTS}`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordModal(false);
      showSuccess('Password updated.');
    } catch (err) {
      setPasswordError(err?.response?.data?.detail || 'Could not update password.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await deleteAccount();
      localStorage.clear();
      await logout();
      navigate('/login');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      setError(`Could not delete account: ${detail}`);
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const tabs = [
    { id: 'practice', label: 'Workspace', icon: '◎' },
    { id: 'zoom', label: 'Integrations', icon: '⇄' },
    { id: 'data', label: 'Data & Privacy', icon: '⚿' },
  ];

  return (
    <>
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.pageTitle}>Settings</div>
          <div style={styles.pageSubtitle}>Configure your workspace and preferences</div>
        </div>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}
      {success && <div style={styles.successAlert}>✓ {success}</div>}

      <div style={styles.layout}>
        {/* Vertical tabs */}
        <div style={styles.tabsCol}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab.id)}
            >
              <span style={styles.tabIcon}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}

          {/* User info at bottom */}
          <div style={styles.userCard}>
            <div style={styles.userAvatar}>
              {(user?.user_metadata?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{user?.user_metadata?.full_name || 'Practitioner'}</div>
              <div style={styles.userEmail}>{user?.email}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>

          {/* PRACTICE TAB */}
          {activeTab === 'practice' && (
            <div>
              <div style={styles.card}>
                <div style={styles.cardHeader}><span style={styles.cardTitle}>Workspace profile</span></div>
                <div style={styles.cardBody}>
                  {loading ? (
                    <div style={styles.loadingState}><LoadingLeaf size={48} /></div>
                  ) : profile ? (
                    <>
                      <div style={styles.formRow}>
                        <div style={{ ...styles.formGroup, flex: 1 }}>
                          <label style={styles.label}>Business name</label>
                          <input
                            style={styles.input}
                            value={profile.practice_name || ''}
                            onChange={e => setProfile({ ...profile, practice_name: e.target.value })}
                            placeholder="e.g. Clarity Coaching"
                          />
                        </div>
                        <div style={{ ...styles.formGroup, flex: 1 }}>
                          <label style={styles.label}>Your name</label>
                          <input
                            style={styles.input}
                            value={profile.full_name || ''}
                            onChange={e => setProfile({ ...profile, full_name: e.target.value })}
                            placeholder="Your full name"
                          />
                        </div>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Timezone</label>
                        <select
                          style={styles.input}
                          value={profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                          onChange={e => setProfile({ ...profile, timezone: e.target.value })}
                        >
                          {timezoneOptions.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                        <div style={styles.hint}>Used to keep session dates and scheduling accurate for you.</div>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>How do you work with clients?</label>
                        <select
                          style={styles.input}
                          value={profile.session_focus || 'both'}
                          onChange={e => setProfile({ ...profile, session_focus: e.target.value })}
                        >
                          <option value="individual">Just 1:1 sessions</option>
                          <option value="group">Groups, courses, or cohorts</option>
                          <option value="both">Both — 1:1 and group/cohort work</option>
                        </select>
                        <div style={styles.hint}>
                          If you only lead 1:1 sessions, we'll hide Groups from your sidebar and session forms so there's less to navigate. You can change this any time.
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Client onboarding questions */}
              <div style={styles.card}>
                <div style={styles.cardHeader}><span style={styles.cardTitle}>Client onboarding questions</span></div>
                <div style={styles.cardBody}>
                  <div style={styles.hint}>
                    What each client is asked, in their own words, the first time they log into their portal. Their answers show at the top of their profile and help the AI understand what they're working toward.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                    {questions.map((q, i) => (
                      <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '8px', background: 'var(--cream)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingTop: '4px' }}>
                          <button style={styles.reorderBtn} disabled={i === 0} onClick={() => moveQuestion(i, -1)} aria-label="Move up">↑</button>
                          <button style={styles.reorderBtn} disabled={i === questions.length - 1} onClick={() => moveQuestion(i, 1)} aria-label="Move down">↓</button>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <input
                            style={{ ...styles.input, fontWeight: '500' }}
                            value={q.question}
                            placeholder="Question"
                            onChange={e => editQuestions(qs => qs.map((x, k) => (k === i ? { ...x, question: e.target.value } : x)))}
                          />
                          <input
                            style={{ ...styles.input, fontSize: '12.5px' }}
                            value={q.hint}
                            placeholder="Helper text (optional)"
                            onChange={e => editQuestions(qs => qs.map((x, k) => (k === i ? { ...x, hint: e.target.value } : x)))}
                          />
                        </div>
                        <button
                          style={{ padding: '4px 10px', borderRadius: '6px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: questions.length <= 1 ? 'not-allowed' : 'pointer', opacity: questions.length <= 1 ? 0.5 : 1, marginTop: '6px' }}
                          disabled={questions.length <= 1}
                          title={questions.length <= 1 ? 'Keep at least one question' : undefined}
                          onClick={() => editQuestions(qs => qs.filter((_, k) => k !== i))}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button
                      style={{ ...styles.ghostBtn, opacity: questions.length >= MAX_QUESTIONS ? 0.5 : 1 }}
                      disabled={questions.length >= MAX_QUESTIONS}
                      onClick={() => editQuestions(qs => [...qs, { question: '', hint: '' }])}
                    >
                      + Add question
                    </button>
                    {questionsDirty && (
                      <button style={styles.saveBtn} onClick={handleSaveQuestions} disabled={savingQuestions || !questions.some(q => q.question.trim())}>
                        {savingQuestions ? 'Saving...' : 'Save questions'}
                      </button>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-soft)' }}>
                      {questions.length} of {MAX_QUESTIONS} · keeps first login light
                    </span>
                  </div>
                </div>
              </div>

              {/* 1:1 Session types */}
              <div style={styles.card}>
                <div style={styles.cardHeader}><span style={styles.cardTitle}>1:1 session types</span></div>
                <div style={styles.cardBody}>
                  <div style={styles.hint}>
                    Reusable names for the individual calls you offer — e.g. "1:1 Activation Call", "Discovery Call", "EMDR". Pick one when logging a session to set its title; leave it unset and sessions keep their AI-generated title as before.
                  </div>
                  {sessionTypes.length === 0 ? (
                    <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginTop: '8px' }}>No 1:1 session types yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '10px', marginBottom: '4px' }}>
                      {sessionTypes.map(st => (
                        <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 12px', borderRadius: '8px', background: 'var(--cream)' }}>
                          <span style={{ flex: 1, fontSize: '13.5px', fontWeight: '500', color: 'var(--text-dark)' }}>{st.name}</span>
                          <button
                            style={{ padding: '4px 10px', borderRadius: '6px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' }}
                            onClick={() => handleRemoveSessionType(st.id)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {showAddTypeRow ? (
                    <div style={{ marginTop: '10px' }}>
                      {groupTypeOptions.length > 0 && (
                        <select
                          style={{ ...styles.input, marginBottom: '8px' }}
                          value={copyFromId}
                          onChange={e => {
                            setCopyFromId(e.target.value);
                            const opt = groupTypeOptions.find(o => o.id === e.target.value);
                            if (opt) setNewTypeName(opt.name);
                          }}
                        >
                          <option value="">Or copy from an existing type…</option>
                          {groupTypeOptions.map(o => <option key={o.id} value={o.id}>{o.groupName}: {o.name}</option>)}
                        </select>
                      )}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          style={styles.input}
                          value={newTypeName}
                          onChange={e => setNewTypeName(e.target.value)}
                          placeholder="e.g. Discovery Call"
                          autoFocus
                          onKeyDown={e => e.key === 'Enter' && handleAddSessionType()}
                        />
                        <button style={styles.saveBtn} onClick={handleAddSessionType} disabled={!newTypeName.trim()}>Add</button>
                        <button style={styles.ghostBtn} onClick={() => { setShowAddTypeRow(false); setNewTypeName(''); setCopyFromId(''); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button style={{ ...styles.ghostBtn, marginTop: '10px' }} onClick={() => setShowAddTypeRow(true)}>+ Add session type</button>
                  )}
                </div>
              </div>

              {/* Offerings */}
              <div style={styles.card}>
                <div style={styles.cardHeader}><span style={styles.cardTitle}>Offerings</span></div>
                <div style={styles.cardBody}>
                  <div style={styles.hint}>
                    What you offer clients — e.g. "1:1 Executive Coaching", "Group Breathwork Cohort". Each session will need to pick one going forward; its modalities pre-fill that session's focus (still editable per session).
                  </div>
                  {offerings.length === 0 ? (
                    <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginTop: '8px' }}>No offerings yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px', marginBottom: '4px' }}>
                      {offerings.map(o => (
                        <div key={o.id} style={{ padding: '12px 14px', borderRadius: '8px', background: 'var(--cream)', opacity: o.is_active ? 1 : 0.55 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ flex: 1, fontSize: '13.5px', fontWeight: '500', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {o.name}
                              {o.format && (
                                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: o.format === 'group' ? 'var(--border-light)' : 'var(--gold-pale)', color: o.format === 'group' ? 'var(--text-mid)' : 'var(--gold)' }}>
                                  {o.format === 'group' ? 'Group' : '1:1'}
                                </span>
                              )}
                              {!o.is_active && <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>(inactive)</span>}
                            </span>
                            <button
                              style={{ padding: '4px 10px', borderRadius: '6px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' }}
                              onClick={() => { setEditingOffering(o); setShowOfferingModal(true); }}
                            >
                              Edit
                            </button>
                            <button
                              style={{ padding: '4px 10px', borderRadius: '6px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' }}
                              onClick={() => handleToggleOfferingActive(o)}
                            >
                              {o.is_active ? 'Deactivate' : 'Reactivate'}
                            </button>
                          </div>
                          {o.description && <div style={{ fontSize: '12.5px', color: 'var(--text-mid)', marginTop: '6px' }}>{o.description}</div>}
                          {(o.modalities || []).length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                              {o.modalities.map(m => (
                                <span key={m} style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '20px', background: 'var(--mint)', color: 'var(--sage)' }}>{m}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button style={{ ...styles.ghostBtn, marginTop: '10px' }} onClick={() => { setEditingOffering(null); setShowOfferingModal(true); }}>+ Add offering type</button>
                </div>
              </div>

              <OfferingFormModal
                open={showOfferingModal}
                onClose={() => { setShowOfferingModal(false); setEditingOffering(null); }}
                onSaved={handleOfferingSaved}
                existingOffering={editingOffering}
                modalityGroups={modalityGroups}
                defaultModalities={selectedModalities}
              />

              {/* Modality selector */}
              {profile && (
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardTitle}>Coaching modalities</span>
                    <span style={styles.selectedBadge}>
                      {selectedModalities.length} selected
                    </span>
                  </div>
                  <div style={styles.cardBody}>
                    <div style={styles.hint}>
                      Select all that apply to your practice — this is your menu, not something applied automatically.
                    </div>
                    <ModalitySelector
                      modalityGroups={modalityGroups}
                      selected={selectedModalities}
                      onToggle={toggleModality}
                      openGroups={openGroups}
                      onToggleGroup={(groupName) => setOpenGroups(o => ({ ...o, [groupName]: !o[groupName] }))}
                    />
                    <div style={styles.hint}>When you start a new session, you'll choose which of these to focus on for that specific session — nothing here gets applied automatically.</div>
                  </div>
                </div>
              )}

              {/* Notifications */}
              {profile && (
                <div style={styles.card}>
                  <div style={styles.cardHeader}><span style={styles.cardTitle}>Notifications</span></div>
                  <div style={styles.cardBody}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Notification email</label>
                      <input
                        style={styles.input}
                        type="email"
                        placeholder="Email for client message notifications"
                        value={profile.notification_email || ''}
                        onChange={e => setProfile({ ...profile, notification_email: e.target.value })}
                      />
                      <div style={styles.hint}>Where to send notifications when clients message you.</div>
                    </div>
                    <div style={styles.toggleRow}>
                      <div
                        style={{ ...styles.toggle, background: profile.notifications_enabled !== false ? 'var(--sage)' : 'var(--border)' }}
                        onClick={() => setProfile({ ...profile, notifications_enabled: profile.notifications_enabled === false })}
                      >
                        <div style={{ ...styles.toggleKnob, transform: profile.notifications_enabled !== false ? 'translateX(20px)' : 'translateX(2px)' }} />
                      </div>
                      <span style={styles.toggleLabel}>
                        {profile.notifications_enabled !== false ? 'Email notifications on' : 'Email notifications off'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button style={styles.saveBtn} onClick={saveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save profile'}
              </button>
            </div>
          )}

          {/* INTEGRATIONS TAB */}
          {activeTab === 'zoom' && (
            <div style={styles.card}>
              <div style={styles.cardHeader}><span style={styles.cardTitle}>Zoom & integrations</span></div>
              <div style={styles.cardBody}>
                <div style={styles.goldAlert}>
                  Transformation Companion can't directly connect to Zoom (requires marketplace approval). Here are the best workflows:
                </div>
                {[
                  { title: 'Zoom auto-transcript', desc: 'After a meeting, go to Recordings → find the meeting → click "Audio Transcript" → copy and paste into the "Paste transcript" tab in New Session.' },
                  { title: 'Otter.ai', desc: 'Auto-joins Zoom calls and produces transcripts. Export as text and paste directly into Transformation Companion.' },
                  { title: 'Fireflies.ai', desc: 'Records and transcribes Zoom, Meet, and Teams calls. Export the transcript and paste it here.' },
                  { title: 'In-person sessions', desc: 'Use the Live Recording tab in New Session — records via your microphone in real time and transcribes with speaker detection.' },
                ].map((item, i, arr) => (
                  <div key={item.title}>
                    <div style={styles.integrationItem}>
                      <div style={styles.integrationTitle}>{item.title}</div>
                      <div style={styles.integrationDesc}>{item.desc}</div>
                    </div>
                    {i < arr.length - 1 && <div style={styles.divider} />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DATA TAB */}
          {activeTab === 'data' && (
            <div>
              <div style={styles.card}>
                <div style={styles.cardHeader}><span style={styles.cardTitle}>Data & privacy</span></div>
                <div style={styles.cardBody}>
                  <div style={styles.infoAlert}>
                    All client data is stored in your private Supabase database with row-level security. Only you can access your data.
                  </div>
                  {[
                    { label: 'Account email', sub: user?.email, action: null },
                    { label: 'Password', sub: 'Change your account password', action: { label: 'Change password', fn: () => { setPasswordError(''); setShowPasswordModal(true); } } },
                    { label: 'Sign out', sub: 'Sign out of your account on this device', action: { label: 'Sign out', fn: handleLogout }, danger: true },
                    { label: 'Delete account', sub: 'Permanently delete your account and all associated data. This cannot be undone.', action: { label: 'Delete account', fn: () => setShowDeleteModal(true) }, danger: true },
                  ].map((item, i, arr) => (
                    <div key={item.label} style={{ ...styles.dataRow, ...(i === arr.length - 1 ? { borderBottom: 'none' } : {}) }}>
                      <div style={styles.dataRowInfo}>
                        <div style={styles.dataRowLabel}>{item.label}</div>
                        <div style={styles.dataRowSub}>{item.sub}</div>
                      </div>
                      {item.action && (
                        <button
                          style={{ ...styles.ghostBtn, ...(item.danger ? styles.dangerBtn : {}) }}
                          onClick={item.action.fn}
                        >
                          {item.action.label}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
      {showPasswordModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPasswordModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...styles.modalTitle, color: 'var(--text-dark)' }}>Change password</h3>
            {passwordError && <div style={{ ...styles.errorAlert, marginBottom: '16px' }}>{passwordError}</div>}
            <p style={styles.modalPrompt}>Current password</p>
            <input
              style={{ ...styles.modalInput, marginBottom: '16px' }}
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
            <p style={styles.modalPrompt}>New password</p>
            <input
              style={styles.modalInput}
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
            <div style={{ ...styles.modalHint, marginBottom: '16px' }}>{PASSWORD_REQUIREMENTS}</div>
            <p style={styles.modalPrompt}>Confirm new password</p>
            <input
              style={{ ...styles.modalInput, marginBottom: '20px' }}
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={{ ...styles.deleteConfirmBtn, background: 'var(--sage)', opacity: (currentPassword && newPassword && confirmPassword && !changingPassword) ? 1 : 0.5 }}
                onClick={handleChangePassword}
                disabled={!currentPassword || !newPassword || !confirmPassword || changingPassword}
              >
                {changingPassword ? 'Updating…' : 'Update password'}
              </button>
              <button
                style={styles.cancelBtn}
                onClick={() => { setShowPasswordModal(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordError(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Delete your account?</h3>
            <p style={styles.modalBody}>
              This will permanently delete your account and all associated data — clients, sessions, messages, recordings, and files. <strong>This cannot be undone.</strong>
            </p>
            <p style={styles.modalPrompt}>Type <strong>DELETE</strong> to confirm:</p>
            <input
              style={{ ...styles.modalInput, borderColor: deleteConfirmText === 'DELETE' ? 'var(--red)' : 'var(--border)', marginBottom: '20px' }}
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={{ ...styles.deleteConfirmBtn, opacity: deleteConfirmText === 'DELETE' && !deleting ? 1 : 0.4 }}
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, delete everything'}
              </button>
              <button style={styles.cancelBtn} onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif" },
  header: { marginBottom: '32px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px' },
  successAlert: { background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px', fontWeight: '500' },
  layout: { display: 'flex', gap: '28px', alignItems: 'flex-start' },
  tabsCol: { width: '180px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  tab: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: 'none', background: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', color: 'var(--text-soft)', cursor: 'pointer', textAlign: 'left', width: '100%' },
  tabActive: { background: 'white', color: 'var(--text-dark)', fontWeight: '600', boxShadow: 'var(--shadow-sm)' },
  tabIcon: { fontSize: '14px', color: 'var(--sage)', width: '16px', textAlign: 'center' },
  userCard: { paddingTop: '24px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', marginTop: '24px' },
  userAvatar: { width: '32px', height: '32px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
  userInfo: { minWidth: 0 },
  userName: { fontSize: '12.5px', fontWeight: '500', color: 'var(--text-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userEmail: { fontSize: '11px', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  content: { flex: 1, minWidth: 0 },
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', marginBottom: '20px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '16px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)' },
  selectedBadge: { background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11.5px', fontWeight: '500', padding: '3px 10px' },
  cardBody: { padding: '24px' },
  formGroup: { marginBottom: '20px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '6px', lineHeight: 1.5 },
  toggleRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  toggle: { width: '44px', height: '24px', borderRadius: '12px', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 },
  toggleKnob: { position: 'absolute', top: '2px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
  toggleLabel: { fontSize: '14px', color: 'var(--text-dark)' },
  infoAlert: { background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px', lineHeight: 1.6 },
  goldAlert: { background: 'var(--gold-pale)', color: '#7a5a18', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px', border: '1px solid rgba(201,168,76,0.3)', lineHeight: 1.6 },
  integrationItem: { padding: '16px 0' },
  integrationTitle: { fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '6px' },
  integrationDesc: { fontSize: '13.5px', color: 'var(--text-mid)', lineHeight: 1.7 },
  divider: { borderTop: '1px solid var(--border-light)' },
  dataRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 0', borderBottom: '1px solid var(--border-light)' },
  dataRowInfo: { flex: 1 },
  dataRowLabel: { fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '2px' },
  dataRowSub: { fontSize: '12px', color: 'var(--text-soft)', lineHeight: 1.5 },
  saveBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  reorderBtn: { width: '22px', height: '18px', padding: 0, lineHeight: 1, borderRadius: '4px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontSize: '11px', cursor: 'pointer' },
  ghostBtn: { padding: '10px 16px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
  dangerBtn: { borderColor: 'rgba(194,94,94,0.3)', color: 'var(--red)' },
  link: { color: 'var(--sage)', textDecoration: 'none' },
  loadingState: { padding: '40px', textAlign: 'center', color: 'var(--text-soft)', fontSize: '14px' },
  modalInput: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '14px', color: 'var(--text-dark)', fontFamily: "'DM Sans', sans-serif", background: '#fff', outline: 'none' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { background: '#fff', borderRadius: '14px', padding: '32px', maxWidth: '420px', width: '100%', boxShadow: 'var(--shadow-lg)' },
  modalTitle: { fontFamily: "'Lora', serif", fontSize: '20px', fontWeight: '600', color: 'var(--red)', margin: '0 0 12px' },
  modalBody: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.6, margin: '0 0 20px' },
  modalPrompt: { fontSize: '13px', color: 'var(--text-mid)', marginBottom: '8px' },
  modalHint: { fontSize: '12px', color: 'var(--text-faint)', lineHeight: 1.5 },
  deleteConfirmBtn: { padding: '11px 20px', borderRadius: '8px', background: 'var(--red)', border: 'none', color: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '600', cursor: 'pointer', flex: 1 },
  cancelBtn: { padding: '11px 20px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', cursor: 'pointer' },
};